import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Rocket, CheckCircle2, Activity, ShieldAlert, AlarmClock, AlertTriangle, Gauge, CalendarClock, Pencil } from "lucide-react";
import { StatCard } from "@/components/common/StatCard";
import { EntryModal } from "@/components/common/EntryModal";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useUser } from "@/context/UserContext";

export const LaunchSummary = ({ refreshKey, onSettingsChange }) => {
  const { canWrite } = useUser();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setS(await api.list("launch-summary")); } catch { setS(null); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const cards = [
    { label: "Total Milestones", value: s?.total ?? "—", icon: Rocket, tone: "royal" },
    { label: "Complete", value: s?.complete ?? "—", icon: CheckCircle2, tone: "jungle" },
    { label: "In Progress", value: s?.in_progress ?? "—", icon: Activity, tone: "gold" },
    { label: "Blocked", value: s?.blocked ?? "—", icon: ShieldAlert, tone: "coral" },
    { label: "Overdue", value: s?.overdue ?? "—", icon: AlarmClock, tone: "orange" },
    { label: "Critical Overdue", value: s?.critical_overdue ?? "—", icon: AlertTriangle, tone: "coral" },
    { label: "Weighted Readiness", value: s ? `${s.readiness}%` : "—", icon: Gauge, tone: "teal" },
    { label: "Days To Launch", value: s?.days_remaining ?? "—", icon: CalendarClock, hint: s?.target_launch_date, tone: "cyan" },
  ];

  const saveSettings = async (v) => {
    await api.updateLaunchSettings({ target_launch_date: v.target_launch_date });
    toast.success("Launch date updated");
    await load();
    onSettingsChange?.();
  };

  return (
    <div data-testid="launch-summary">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5A6B8C]">
          Launch target · {s?.target_launch_date || "—"}
        </p>
        {canWrite && <Button size="sm" variant="outline" data-testid="launch-settings-btn" onClick={() => setOpen(true)} className="rounded-full border-[#E4E9F3]">
          <Pencil size={14} /> Launch Settings
        </Button>}
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
        {cards.map((c, i) => <StatCard key={c.label} index={i} testid={`launch-stat-${i}`} {...c} />)}
      </div>

      <EntryModal
        open={open}
        onOpenChange={setOpen}
        title="Launch Settings"
        description="Set the target launch date for Edju Cat."
        submitLabel="Save Date"
        fields={[{ name: "target_launch_date", label: "Target launch date", type: "date", required: true }]}
        initialValues={{ target_launch_date: s?.target_launch_date }}
        onSubmit={saveSettings}
      />
    </div>
  );
};
